import * as Api from "@api";
import * as C from "@components";
import clsx from "clsx";
import React from "react";
import { ProductOptionsCardKeys } from "@/parts";
import css from "./product-options.module.scss";

export const enum ProductOptionsCardGap
{
    G0,
    G10,
    G20,
    G40,
    G60,
    G80,
    G120,
    G200,
}

const gapClassMap: Record<ProductOptionsCardGap, string> = {
    [ProductOptionsCardGap.G0]: "0",
    [ProductOptionsCardGap.G10]: "10",
    [ProductOptionsCardGap.G20]: "20",
    [ProductOptionsCardGap.G40]: "40",
    [ProductOptionsCardGap.G60]: "60",
    [ProductOptionsCardGap.G80]: "80",
    [ProductOptionsCardGap.G120]: "120",
    [ProductOptionsCardGap.G200]: "200",
};

interface ProductOptionsCardLayout extends Omit<C.CardLayout, "variant">, C.LayoutEntity<C.Width, "itemWidth">, C.LayoutEntity<ProductOptionsCardGap, "gap"> { }

interface ProductOptionsCardProps<ContentKeys extends readonly string[]> extends Omit<React.ComponentProps<typeof C.Card>, "layout">
{
    footerProps?: Partial<C.PageContentProps>;
    footerDetailsProps?: Partial<C.PageContentProps>;
    footerNode?: React.ReactNode;
    headlineProps?: Partial<C.PageContentProps>;
    imageDirectory: string;
    layout?: C.MediaSpecific<ProductOptionsCardLayout>;
    options: C.CaptionedImageProps[] | {
        captionProps?: Partial<Omit<C.PageContentProps, "data" | "type">>;
        contentKeys: ContentKeys;
        contentAltKeys?: string[];
        imageHeight?: number;
        imageHeights?: number[];
        imageWidth?: number;
        imageWidths?: number[];
        imageMobileWidth?: number;
        imageMobileWidths?: number[];
        imageMobileHeight?: number;
        imageMobileHeights?: number[];
        imagePath?: string[];
        imageClassName?: string;
        productSlug?: string;
        optionWidth?: number;
    };
    pageContent: Api.Content<ContentKeys[number] | typeof ProductOptionsCardKeys[number]>;
    isWrapper?: boolean
}

export const ProductOptionsCard = <ContentKeys extends readonly string[]>({ className, footerProps, footerDetailsProps, footerNode, headlineProps, imageDirectory, layout, options, pageContent, isWrapper, ...props }: ProductOptionsCardProps<ContentKeys>): React.ReactComponentElement<typeof C.Card, ProductOptionsCardProps<ContentKeys>> =>
{
    const cardClasses = clsx(
        css["card"],
        className
    );

    const listClasses = clsx(
        css["list"],
        C.mediaSpecificClassM(layout, "gap", "g", gapClassMap, c => css[c])
    );

    const headlinePropsApplied: C.PageContentProps = {
        ...Api.ContentService.getItem(pageContent, "options.headline") as Api.ContentItem,
        typography: C.Typography.H5,
        ...headlineProps,
    };

    const footer = Api.ContentService.getItem(pageContent, "options.footer");
    const footerDetails = Api.ContentService.getItem(pageContent, "options.footer-details");

    const footerPropsApplied: C.PageContentProps | undefined = footer !== undefined ?
        {
            ...footer,
            centered: true,
            typography: C.Typography.P5Caption,
            margins: { top: C.Margin.D30 },
            ...footerProps,
        }
        :
        undefined;

    const footerDetailsPropsApplied: C.PageContentProps | undefined = footerDetails !== undefined ?
        {
            ...footerDetails,
            centered: true,
            typography: C.Typography.P5Caption,
            margins: { top: C.Margin.D30 },
            ...footerDetailsProps,
        }
        :
        undefined;

    const optionsApplied: C.CaptionedImageProps[] =
        Array.isArray(options)
            ? options.map(option => ({
                ...option,
                layout: C.mediaSpecificMerge(option.layout, "width", layout, "itemWidth"),
            }))
            : options.contentKeys.map((key, idx) => {
                const content = Api.ContentService.getItem(pageContent, key) as Api.ContentItem;
                let contentAlt = "";
                if (!!options.contentAltKeys?.length) {
                    const contentAltKey = options.contentAltKeys.find(alt => alt.startsWith(key)) ?? "";
                    contentAlt = (Api.ContentService.getItem(pageContent, contentAltKey) as Api.ContentItem)?.data ?? "";
                }

                return {
                    captionProps: {
                        ...content,
                        centered: true,
                        margins: { top: C.Margin.D10 },
                        typography: C.Typography.P3,
                        ...options.captionProps
                    },
                    imageProps: {
                        src: `${imageDirectory}${options.imagePath ? options.imagePath[idx] : key}.png`,
                        mobileSrc: `${imageDirectory}${options.imagePath ? options.imagePath[idx] : key}.png`,
                        alt: contentAlt,
                        width: Array.isArray(options.imageWidths) ? options.imageWidths[idx] : options.imageWidth,
                        mobileWidth: Array.isArray(options.imageMobileWidths) ? options.imageMobileWidths[idx] : options.imageMobileWidth,
                        height: Array.isArray(options.imageHeights) ? options.imageHeights[idx] : options.imageHeight,
                        mobileHeight: Array.isArray(options.imageMobileHeights) ? options.imageMobileHeights[idx] : options.imageMobileHeight,
                        className: options.imageClassName
                    },
                    layout: C.mediaSpecificMerge(undefined, "width", layout, "itemWidth"),
                    width: options.optionWidth,
                };
            });

    return (
        <C.Card {...props} className={cardClasses} layout={layout}>
            <C.PageContent {...headlinePropsApplied} />
            <div className={listClasses}>
                {
                    optionsApplied.map((option, index) =>
                        <div key={index} className={isWrapper ? css["captionedImageWrapper"] : ''}>
                            <C.CaptionedImage {...option} />
                        </div>
                    )
                }
            </div>
            {
                footerNode ? footerNode : footerPropsApplied !== undefined && <C.PageContent {...footerPropsApplied} />
            }
            {
                footerDetailsPropsApplied !== undefined && <C.PageContent {...footerDetailsPropsApplied} />
            }
        </C.Card>
    );
}

